import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { GatsbyImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { blogBreakPoint } from '../../../constants/breakPoints'
import { breakpoints } from '../../../constants/breakPoints'
import { BlogPostCardProps } from '../../../interfaces/blog'
import { Link } from '../../Link'
import VerticalTextDivider from '../../VerticalTextDivider'
import { BlogAuthorInfo } from './BlogAuthorInfo'

export const BlogPostCard: React.FunctionComponent<BlogPostCardProps> = ({
  slug,
  title,
  abstract,
  heroImage,
  imageWithBorder = false,
  postCategory,
  postAuthor,
  publishedAt,
  createdAt,
  isSmall = false,
  id,
  className,
  onClick,
}) => {
  const date = dayjs(publishedAt || createdAt).format('D MMM YYYY')

  if (isSmall) {
    return (
      <div id={id} className={className}>
        <Link href={`/blog/${slug}/`} css={smallPostContainerStyle(postCategory.categoryColor)} onClick={onClick}>
          <div>
            {postCategory && (
              <Link
                className={'strong-body-1 opacity-on-hover'}
                href={`/blog/category/${postCategory.slug}/`}
                css={categoryLinkStyle(postCategory.categoryColor)}
              >
                {postCategory.name}
                <br />
              </Link>
            )}
            <div className={'strong-body-1 black-text'}>{title}</div>
          </div>

          <p className={'body-2 gray-text'}>
            <Link href={`/blog/author/${postAuthor.slug}/`} className={'opacity-on-hover'}>
              {postAuthor.name}
            </Link>
            <VerticalTextDivider /> {date}
          </p>
        </Link>
      </div>
    )
  }
  return (
    <div id={id} className={className}>
      <Link
        className={'translate-and-shadow-on-hover'}
        href={`/blog/${slug}/`}
        onClick={onClick}
        css={blogPostStyle(imageWithBorder)}
      >
        {heroImage?.gatsbyImageData && (
          <GatsbyImage image={heroImage.gatsbyImageData} alt={'Testimonial Company'} css={heroImageStyle} />
        )}
        <div css={postInfoStyle}>
          <div>
            <div className={'strong-body-2 gray-text'}>
              {postCategory && (
                <Link
                  className={'opacity-on-hover'}
                  href={`/blog/category/${postCategory.slug}/`}
                  css={categoryLinkStyle(postCategory.categoryColor)}
                >
                  {postCategory.name}
                </Link>
              )}{' '}
              <VerticalTextDivider /> {date}
            </div>
            <div className={'heading4 black-text'}>{title}</div>
            <div className={'body-1 black-text'}>{abstract}</div>
          </div>
          <BlogAuthorInfo author={postAuthor} cssStyles={blogAuthorInfoStyle} />
        </div>
      </Link>
    </div>
  )
}

const smallPostContainerStyle = (categoryColor: string) => css`
  display: block;
  border: 1px solid #efeff0;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  text-decoration: none !important;
  color: #000;
  transition: border 0.2s ease-in-out;

  &:hover {
    border: 1px solid ${categoryColor};

    @media screen and (max-width: ${blogBreakPoint}px) {
      box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 20px 0px;
    }
  }

  @media screen and (max-width: ${blogBreakPoint}px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    background-color: #fff;
  }
`

// export for glossary.tsx & glossaryTerm.tsx
export const categoryLinkStyle = (categoryColor: string) => css`
  color: ${categoryColor};
`

const blogPostStyle = (imgWithBorder: boolean) => css`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  overflow: hidden;
  height: 100%;
  position: relative;
  z-index: 0;

  ${imgWithBorder &&
  `
    & > .gatsby-image-wrapper {
      border: 1px solid #efeff0;
      border-bottom: none;
      border-radius: 0.5rem 0.5rem 0 0;
      overflow: hidden;
    }
  `};

  .heading4 {
    margin: 1rem 0;
  }

  .black-text > .body-1 {
    margin-bottom: 1rem;
  }

  @media screen and (min-width: ${breakpoints.small + 1}px) {
    .gatsby-image-wrapper {
      aspect-ratio: 2/1;
    }
  }
`

const heroImageStyle = css`
  max-height: 11.9rem;
`

const postInfoStyle = css`
  padding: 1.5rem;
  border: 1px solid #efeff0;
  border-top: none;
  border-radius: 0 0 0.5rem 0.5rem;
  flex-grow: 1;
  display: flex;
  flex-flow: column;
  align-content: space-between;
`

const blogAuthorInfoStyle = css`
  margin-top: auto;
  padding-top: 1rem;
`
